import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Warehouse, Search, Check, Loader2, Eye, EyeOff, KeyRound, Copy, Upload, Clock, ExternalLink, AlertTriangle, CheckCircle2, Image as ImageIcon, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-md border border-xmove-ink/15 bg-xmove-cream px-2 py-1.5 text-sm text-xmove-ink outline-none focus:border-xmove-orange";

export const fa = (iso) => {
  const m = Math.round((Date.now() - new Date(iso)) / 60000);
  return m < 60 ? `${m} min fa` : m < 1440 ? `${Math.round(m / 60)} ore fa` : new Date(iso).toLocaleString("it-IT");
};

const RigaUsato = ({ a, onSaved }) => {
  const [prezzo, setPrezzo] = useState(a.prezzo ?? "");
  const [esempio, setEsempio] = useState(a.esempio_quotazione ?? "");
  const [cv, setCv] = useState(a.cv ?? "");
  const [saving, setSaving] = useState(false);
  const cambiato = String(prezzo) !== String(a.prezzo ?? "") || esempio !== (a.esempio_quotazione ?? "") || String(cv) !== String(a.cv ?? "");

  const salva = async (extra = {}) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/usato/${a.vehicleid}`, { prezzo: prezzo === "" ? 0 : Number(prezzo), esempio_quotazione: esempio, cv: cv === "" ? 0 : Number(cv), ...extra });
      toast.success(`${a.marca} ${a.modello} aggiornata.`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio fallito.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className={`rounded-xl border bg-white p-3 ${a.visibile ? "border-xmove-ink/8" : "border-dashed border-xmove-ink/20 opacity-70"}`} data-testid={`usato-row-${a.vehicleid}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex h-14 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-xmove-night/90 text-[10px] text-white/60">
          {a.foto ? <img src={a.foto} alt="" className="h-full w-full object-cover" data-testid={`usato-thumb-${a.vehicleid}`} /> : "no foto"}
        </div>
        <div className="min-w-[220px] flex-1">
          <p className="text-sm font-bold text-xmove-ink">{a.marca} {a.modello} <span className="font-normal text-xmove-muted">· {a.anno} · {a.km?.toLocaleString("it-IT")} km · {a.targa}</span></p>
          <p className="mt-0.5 line-clamp-1 text-[11px] text-xmove-muted">{a.versione}</p>
          <p className="mt-1 text-[11px] text-xmove-muted">{a.alimentazione} · {a.cambio}{a.tipo_veicolo && a.tipo_veicolo !== "auto" && <span className="ml-1 rounded bg-xmove-night px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">{a.tipo_veicolo}</span>}{a.link_foto && <a href={a.link_foto} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-0.5 text-xmove-orange hover:underline"><ExternalLink size={10} /> foto</a>}</p>
        </div>
        <label className="block w-32">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Prezzo €</span>
          <input type="number" min="0" value={prezzo} onChange={(e) => setPrezzo(e.target.value)} placeholder="su richiesta" className={inputCls} data-testid={`usato-prezzo-${a.vehicleid}`} />
        </label>
        <label className="block w-24">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">CV</span>
          <input type="number" min="0" value={cv} onChange={(e) => setCv(e.target.value)} placeholder={a.cv_stimati ? `~${a.cv_stimati}` : "n.d."} title="Potenza: se vuoto usa quella stimata dalla descrizione" className={inputCls} data-testid={`usato-cv-${a.vehicleid}`} />
        </label>
        <label className="block w-full sm:w-64">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-xmove-muted">Esempio quotazione</span>
          <input type="text" value={esempio} onChange={(e) => setEsempio(e.target.value)} placeholder="Es. da € 249/mese con anticipo € 3.000, 48 mesi" className={inputCls} data-testid={`usato-esempio-${a.vehicleid}`} />
        </label>
        <div className="flex items-end gap-1.5 self-end">
          <button onClick={() => salva()} disabled={saving || !cambiato} data-testid={`usato-save-${a.vehicleid}`} className="flex items-center gap-1 rounded-lg bg-xmove-orange px-3 py-2 text-xs font-bold text-white hover:bg-xmove-orangeDark disabled:opacity-40">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Salva
          </button>
          <button onClick={() => salva({ visibile: !a.visibile })} disabled={saving} title={a.visibile ? "Nascondi dal sito" : "Mostra sul sito"} data-testid={`usato-toggle-${a.vehicleid}`} className="rounded-lg border border-xmove-ink/15 p-2 text-xmove-muted hover:border-xmove-night hover:text-xmove-night">
            {a.visibile ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
        </div>
      </div>
    </li>
  );
};

export const UsatoGestione = () => {
  const [auto, setAuto] = useState(null);
  const [log, setLog] = useState([]);
  const [stato, setStato] = useState(null);
  const [q, setQ] = useState("");
  const [apiKey, setApiKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [recupero, setRecupero] = useState(false);
  const [riapplicando, setRiapplicando] = useState(false);

  const riapplica = async () => {
    setRiapplicando(true);
    try {
      const r = await axios.post(`${API}/admin/usato/riapplica-ultimo`);
      toast.success(`Sito riallineato: ${r.data.attive} auto attive (${r.data.nuove} nuove, ${r.data.rimosse} rimosse).`);
      load();
    } catch {
      toast.error("Riallineamento fallito.");
    } finally {
      setRiapplicando(false);
    }
  };

  const load = () => {
    axios.get(`${API}/admin/usato`).then((r) => setAuto(r.data)).catch(() => setAuto([]));
    axios.get(`${API}/admin/usato/import-log`).then((r) => setLog(r.data)).catch(() => {});
    axios.get(`${API}/admin/usato/stato`).then((r) => setStato(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const recuperaFoto = async () => {
    setRecupero(true);
    try {
      const r = await axios.post(`${API}/admin/usato/recupera-foto`);
      toast.success(`${r.data.trovate} foto recuperate.`);
      load();
    } catch {
      toast.error("Recupero foto fallito.");
    } finally {
      setRecupero(false);
    }
  };

  const filtrate = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (auto || []).filter((a) => !t || `${a.marca} ${a.modello} ${a.versione} ${a.targa}`.toLowerCase().includes(t));
  }, [auto, q]);

  const mostraKey = () => axios.get(`${API}/admin/usato/api-key`).then((r) => setApiKey(r.data.api_key));
  const copia = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copiato."));

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await axios.post(`${API}/admin/usato/import`, fd);
      toast.success(`Import ok: ${r.data.attive} auto attive (${r.data.nuove} nuove, ${r.data.rimosse} rimosse).`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fallito.");
    } finally {
      setUploading(false);
    }
  };

  const ultimo = log[0];
  const conPrezzo = (auto || []).filter((a) => a.prezzo).length;
  const curl = `curl -X POST ${API}/usato/import -H "X-Api-Key: ${apiKey || "LA_TUA_CHIAVE"}" -F "file=@/percorso/leaseafterReport.csv"`;

  return (
    <section className="mt-14" data-testid="usato-section">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-xmove-orange"><Warehouse size={14} /> Stock usato</p>
      <h2 className="mt-2 font-display text-2xl font-bold text-xmove-ink sm:text-3xl">Auto usate importate</h2>
      <p className="mt-2 max-w-2xl text-sm text-xmove-muted">
        Lo stock arriva dal file del portale (CSV/Excel) tramite API circa ogni ora. Qui inserisci prezzo ed esempio di quotazione: senza prezzo l'auto appare come "Prezzo su richiesta". Le auto sparite dal file escono dal sito automaticamente; i tuoi prezzi non vengono mai sovrascritti.
        Ricevi un'email a ogni auto nuova e se gli import si fermano per più di 2 ore ({stato?.orario || "Lun-Ven 7:00-20:00"}).
      </p>

      {stato && (
        <div
          className={`mt-5 flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-sm ${stato.fermo ? "border-red-300 bg-red-50 text-red-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}
          data-testid={stato.fermo ? "usato-stato-fermo" : "usato-stato-ok"}
        >
          {stato.fermo ? <AlertTriangle size={18} className="shrink-0" /> : <CheckCircle2 size={18} className="shrink-0" />}
          <p className="flex-1">
            {stato.fermo
              ? `Import fermo: ${stato.ultimo_import ? `nessun file ricevuto da ${Math.floor(stato.minuti_da_ultimo / 60)} ore e ${stato.minuti_da_ultimo % 60} minuti` : "nessun import mai ricevuto"}. Controlla il portale e l'automazione.`
              : stato.ultimo_import
                ? `Import regolare: ultimo file ${fa(stato.ultimo_import)}${stato.in_orario ? "" : " (fuori orario lavorativo, nessun controllo attivo)"}.`
                : "Nessun import ancora ricevuto."}
            {stato.email_avvisi_attive === false && <span className="ml-1 text-xs opacity-80" data-testid="usato-avvisi-anteprima">(ambiente di anteprima: le email di avviso partono solo dal sito pubblicato)</span>}
          </p>
          {stato.senza_foto > 0 && (
            <button onClick={recuperaFoto} disabled={recupero} data-testid="usato-recupera-foto-btn" className="flex items-center gap-1.5 rounded-lg border border-current/30 bg-white px-3 py-1.5 text-xs font-bold hover:border-xmove-orange hover:text-xmove-orange disabled:opacity-50">
              {recupero ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />} Recupera {stato.senza_foto} foto mancanti
            </button>
          )}
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-xmove-ink shadow-sm" data-testid="usato-admin-count">{auto ? auto.length : "…"} auto in stock</span>
        <span className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-emerald-600 shadow-sm">{conPrezzo} con prezzo</span>
        {ultimo && (
          <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-xmove-muted shadow-sm" data-testid="usato-last-import">
            <Clock size={12} /> ultimo import {fa(ultimo.quando)} ({ultimo.origine}: {ultimo.righe} righe, {ultimo.nuove} nuove, {ultimo.rimosse} rimosse)
          </span>
        )}
        <span className="flex-1" />
        {stato?.ha_ultimo_file && (
          <button onClick={riapplica} disabled={riapplicando} data-testid="usato-riapplica-btn" title="Riallinea il sito all'ultimo file ricevuto" className="flex items-center gap-1.5 rounded-lg border border-xmove-ink/15 bg-white px-3 py-2 text-xs font-bold text-xmove-ink hover:border-xmove-orange hover:text-xmove-orange disabled:opacity-50">
            {riapplicando ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Riallinea all'ultimo file
          </button>
        )}
        <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-xmove-ink/15 bg-white px-3 py-2 text-xs font-bold text-xmove-ink hover:border-xmove-orange hover:text-xmove-orange">
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Importa file ora
          <input type="file" accept=".csv,.xlsx,.xls" onChange={upload} className="hidden" data-testid="usato-upload-input" />
        </label>
        <button onClick={apiKey ? () => setApiKey(null) : mostraKey} data-testid="usato-apikey-btn" className="flex items-center gap-1.5 rounded-lg border border-xmove-ink/15 bg-white px-3 py-2 text-xs font-bold text-xmove-ink hover:border-xmove-orange hover:text-xmove-orange">
          <KeyRound size={13} /> {apiKey ? "Nascondi chiave API" : "Chiave API per l'automazione"}
        </button>
      </div>

      {apiKey && (
        <div className="mt-3 rounded-xl border border-xmove-orange/30 bg-white p-4 text-xs" data-testid="usato-apikey-box">
          <p className="font-bold text-xmove-ink">Chiave API (header <code>X-Api-Key</code>) — non condividerla</p>
          <div className="mt-1 flex items-center gap-2"><code className="flex-1 break-all rounded bg-xmove-cream px-2 py-1 text-[11px]" data-testid="usato-apikey-value">{apiKey}</code><button onClick={() => copia(apiKey)} className="rounded border border-xmove-ink/15 p-1.5 hover:text-xmove-orange" title="Copia"><Copy size={12} /></button></div>
          <p className="mt-3 font-bold text-xmove-ink">Comando da far eseguire a ogni download (circa ogni ora) (sostituisci il percorso del file):</p>
          <div className="mt-1 flex items-center gap-2"><code className="flex-1 break-all rounded bg-xmove-cream px-2 py-1 text-[11px]">{curl}</code><button onClick={() => copia(curl)} className="rounded border border-xmove-ink/15 p-1.5 hover:text-xmove-orange" title="Copia"><Copy size={12} /></button></div>
        </div>
      )}

      <label className="relative mt-4 block">
        <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-xmove-muted" />
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca marca, modello, targa…" data-testid="usato-admin-search" className="w-full rounded-xl border border-xmove-ink/10 bg-white py-3 pl-10 pr-4 text-sm text-xmove-ink outline-none focus:border-xmove-orange" />
      </label>

      {auto === null ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-xmove-orange" size={22} /></div>
      ) : filtrate.length === 0 ? (
        <p className="py-10 text-center text-sm text-xmove-muted" data-testid="usato-admin-empty">Nessuna auto in stock{q ? " per questa ricerca" : ": importa il primo file"}.</p>
      ) : (
        <ul className="mt-4 space-y-2" data-testid="usato-admin-list">
          {filtrate.map((a) => <RigaUsato key={`${a.vehicleid}-${a.prezzo}-${a.esempio_quotazione}-${a.visibile}`} a={a} onSaved={load} />)}
        </ul>
      )}
    </section>
  );
};
